/**
 * High-quality crop + scale into a fresh canvas — the shared core of
 * image-editor's `cropCanvasToSlice` (slice export) and
 * `scaleRegionCanvas` (region export), which were near-identical copies.
 *
 * Contract notes:
 *  - ALWAYS returns a fresh canvas (no identity short-circuit for
 *    `scale === 1`): callers rely on "safe to encode/dispose without
 *    mutating the source" semantics. A caller that wants the common-case
 *    alloc saving keeps its own `if (scale === 1) return canvas;`.
 *  - Throws `CanvasContextError` when `getContext('2d')` is unavailable
 *    rather than silently returning a blank or the original — the two
 *    pre-extraction copies disagreed here (blank vs. original), so each
 *    call site preserves its legacy degenerate-path behavior with an
 *    explicit `catch`.
 *  - Invalid numeric options are programmer error and throw `RangeError`
 *    (NaN survives `Math.max(1, Math.round(NaN))`, so garbage input would
 *    otherwise produce a broken canvas far from the bug).
 */

/** `document.createElement('canvas').getContext('2d')` returned null —
 *  the environment can't 2D-composite at all. */
export class CanvasContextError extends Error {
  constructor() {
    super('Canvas 2D context unavailable — cannot crop/scale.');
    this.name = 'CanvasContextError';
  }
}

export interface CropScaleOptions {
  /** Crop rect in source pixels. Default: the full source canvas. */
  sourceRect?: { x: number; y: number; width: number; height: number };
  /** Uniform scale applied to the source-rect size. Default 1. */
  scale?: number;
}

function assertPositiveFinite(value: number, label: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(
      `cropScaleCanvas: ${label} must be a positive finite number (got ${value})`,
    );
  }
}

/**
 * Crop `sourceRect` out of `source` and draw it, scaled by `scale`, into a
 * fresh canvas sized `Math.max(1, Math.round(rect * scale))` per side, with
 * `imageSmoothingQuality: 'high'` so 2×/3× upscales look reasonable
 * without halos.
 */
export function cropScaleCanvas(
  source: HTMLCanvasElement,
  options?: CropScaleOptions,
): HTMLCanvasElement {
  const scale = options?.scale ?? 1;
  assertPositiveFinite(scale, 'scale');
  const rect = options?.sourceRect ?? { x: 0, y: 0, width: source.width, height: source.height };
  assertPositiveFinite(rect.width, 'sourceRect.width');
  assertPositiveFinite(rect.height, 'sourceRect.height');
  if (!Number.isFinite(rect.x) || !Number.isFinite(rect.y)) {
    throw new RangeError(
      `cropScaleCanvas: sourceRect.x/y must be finite numbers (got ${rect.x}, ${rect.y})`,
    );
  }

  const outW = Math.max(1, Math.round(rect.width * scale));
  const outH = Math.max(1, Math.round(rect.height * scale));
  const out = document.createElement('canvas');
  out.width = outW;
  out.height = outH;
  const ctx = out.getContext('2d');
  if (!ctx) throw new CanvasContextError();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, rect.x, rect.y, rect.width, rect.height, 0, 0, outW, outH);
  return out;
}
